import {
  Autocomplete,
  type AutocompleteProps,
  Combobox,
  Group,
  Highlight,
  Loader,
  MultiSelect,
  type MultiSelectProps,
  NumberInput,
  type NumberInputProps,
  Select,
  type SelectProps,
  Text,
  TextInput,
  type TextInputProps,
  useCombobox,
} from '@mantine/core'
import {
  DateInput,
  type DateInputProps,
  DatePickerInput,
  type DatePickerInputProps,
  DateTimePicker,
  type DateTimePickerProps,
  TimeInput,
  type TimeInputProps,
} from '@mantine/dates'
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks'
import { dateFormat, dayjs, type Locale, parseThaiDate } from '@tnn/core'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

export interface Option {
  value: string
  label: string
  description?: string
}

/** Runs a loader whenever the (debounced) query changes, ignoring stale replies. */
function useRemote<T>(query: string, load: (q: string) => Promise<T[]>, enabled = true) {
  const [debounced] = useDebouncedValue(query, 180)
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!enabled) return
    let stale = false
    setLoading(true)
    load(debounced)
      .then((result) => !stale && setItems(result))
      .catch(() => !stale && setItems([]))
      .finally(() => !stale && setLoading(false))
    return () => {
      stale = true
    }
  }, [debounced, load, enabled])
  return { items, loading }
}

export interface SuggestInputProps extends Omit<AutocompleteProps, 'data' | 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  /** Returns values already used in this field that match the query */
  load: (query: string) => Promise<string[]>
}

/**
 * Free-text autocomplete: suggests values people typed before (origins,
 * cargo, line descriptions…) but accepts anything new.
 */
export function SuggestInput({ value, onChange, load, ...rest }: SuggestInputProps) {
  const [focused, setFocused] = useState(false)
  const { items, loading } = useRemote(value ?? '', load, focused)
  return (
    <Autocomplete
      {...rest}
      value={value ?? ''}
      onChange={onChange}
      data={items}
      limit={8}
      onFocus={(e) => {
        setFocused(true)
        rest.onFocus?.(e)
      }}
      rightSection={loading && focused ? <Loader size={14} /> : rest.rightSection}
      renderOption={({ option }) => (
        <Highlight highlight={value ?? ''} size="sm">
          {option.value}
        </Highlight>
      )}
    />
  )
}

export interface PickOption<T> extends Option {
  item: T
  /** A small sign after the name (an icon: there is something good to know) */
  mark?: ReactNode
}

export interface PickInputProps<T> extends Omit<TextInputProps, 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  /** Matches for what was typed; each carries the record it stands for */
  load: (query: string) => Promise<PickOption<T>[]>
  /** A match was chosen: copy what it carries into the neighbouring fields */
  onPick: (option: PickOption<T>) => void
}

/**
 * Free text that can also pick a known record: typing a name offers the
 * matching contacts, products or places, and choosing one fills in the rest
 * (phone, address, rate…). Anything new is kept as typed.
 */
export function PickInput<T>({ value, onChange, load, onPick, readOnly, ...rest }: PickInputProps<T>) {
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() })
  const [focused, setFocused] = useState(false)
  const { items, loading } = useRemote(value ?? '', load, focused && !readOnly)
  const open = () => !readOnly && combobox.openDropdown()
  const words = (value ?? '').trim().split(/\s+/).filter(Boolean)

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(key) => {
        const option = items.find((o) => o.value === key)
        if (option) onPick(option)
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <TextInput
          {...rest}
          readOnly={readOnly}
          autoComplete="off"
          value={value ?? ''}
          onChange={(e) => {
            onChange(e.currentTarget.value)
            open()
            combobox.updateSelectedOptionIndex()
          }}
          onClick={open}
          onFocus={(e) => {
            setFocused(true)
            open()
            rest.onFocus?.(e)
          }}
          onBlur={(e) => {
            combobox.closeDropdown()
            rest.onBlur?.(e)
          }}
          rightSection={loading && focused ? <Loader size={14} /> : rest.rightSection}
        />
      </Combobox.Target>
      <Combobox.Dropdown hidden={items.length === 0}>
        <Combobox.Options mah={320} style={{ overflowY: 'auto' }}>
          {items.map((o) => (
            <Combobox.Option key={o.value} value={o.value}>
              <Group gap={6} wrap="nowrap">
                <Highlight highlight={words} size="sm">
                  {o.label}
                </Highlight>
                {o.mark}
              </Group>
              {o.description && (
                <Text size="xs" c="dimmed">
                  {o.description}
                </Text>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export interface RemoteSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchValue' | 'onSearchChange'> {
  value: string | null
  onChange: (value: string | null) => void
  /** Search records for the dropdown */
  load: (query: string) => Promise<Option[]>
  /** Resolve labels of already chosen ids */
  resolve: (ids: string[]) => Promise<Option[]>
}

/**
 * Relation picker: searches another resource on the server as you type. Opened, it searches afresh with the chosen
 * label greyed as the placeholder; read-only it never opens (Mantine still "opens" a read-only Select on a click, which
 * would blank the label).
 */
export function RemoteSelect({ value, onChange, load, resolve, ...rest }: RemoteSelectProps) {
  const [search, setSearch] = useState('')
  const [opened, setOpened] = useState(false)
  const [selected, setSelected] = useState<Option | null>(null)
  const { items, loading } = useRemote(search, load, opened)

  useEffect(() => {
    if (!value) return setSelected(null)
    if (selected?.value === value) return
    let stale = false
    resolve([value]).then((r) => !stale && setSelected(r[0] ?? { value, label: value }))
    return () => {
      stale = true
    }
  }, [value, resolve, selected?.value])

  const data = useMemo(() => {
    const list = [...items]
    if (selected && !list.some((o) => o.value === selected.value)) list.unshift(selected)
    return list
  }, [items, selected])

  return (
    <Select
      {...rest}
      searchable
      clearable={!rest.required}
      value={value}
      data={data}
      filter={({ options }) => options}
      searchValue={opened ? search : (selected?.label ?? '')}
      placeholder={opened && !search && selected ? selected.label : rest.placeholder}
      onSearchChange={(q) => opened && setSearch(q)}
      onDropdownOpen={() => {
        if (rest.readOnly) return
        setSearch('')
        setOpened(true)
      }}
      onDropdownClose={() => setOpened(false)}
      onChange={(v) => {
        setSelected(data.find((o) => o.value === v) ?? null)
        onChange(v)
      }}
      rightSection={loading && opened ? <Loader size={14} /> : undefined}
      renderOption={({ option }) => {
        const o = data.find((d) => d.value === option.value)
        return (
          <div>
            <div>{option.label}</div>
            {o?.description && <div style={{ fontSize: 12, color: 'var(--tnn-on-surface-variant)' }}>{o.description}</div>}
          </div>
        )
      }}
    />
  )
}

export interface RemoteMultiSelectProps extends Omit<MultiSelectProps, 'data' | 'value' | 'onChange'> {
  value: string[]
  onChange: (value: string[]) => void
  load: (query: string) => Promise<Option[]>
  resolve: (ids: string[]) => Promise<Option[]>
}

export function RemoteMultiSelect({ value, onChange, load, resolve, ...rest }: RemoteMultiSelectProps) {
  const [search, setSearch] = useState('')
  const [known, setKnown] = useState<Option[]>([])
  const { items } = useRemote(search, load)
  const key = value.join(',')

  useEffect(() => {
    if (!key) return
    let stale = false
    resolve(key.split(',')).then((r) => !stale && setKnown((prev) => [...prev.filter((p) => !r.some((o) => o.value === p.value)), ...r]))
    return () => {
      stale = true
    }
  }, [key, resolve])

  const data = useMemo(() => {
    const map = new Map<string, Option>()
    for (const o of [...known, ...items]) map.set(o.value, o)
    return [...map.values()]
  }, [known, items])

  return (
    <MultiSelect
      {...rest}
      searchable
      value={value}
      data={data}
      searchValue={search}
      onSearchChange={setSearch}
      onChange={(v) => {
        setKnown((prev) => [...prev, ...data.filter((o) => v.includes(o.value))])
        onChange(v)
      }}
    />
  )
}

export interface DateFieldProps extends Omit<DateInputProps, 'valueFormat' | 'dateParser'> {
  locale: Locale
}

/** Calendar headings in Buddhist-era years in Thai (ตุลาคม 2569), Gregorian in English */
function calendarLabels(
  locale: Locale,
): Pick<DateInputProps, 'monthLabelFormat' | 'yearLabelFormat' | 'yearsListFormat' | 'decadeLabelFormat'> {
  const year = locale === 'th' ? 'BBBB' : 'YYYY'
  return {
    monthLabelFormat: `MMMM ${year}`,
    yearLabelFormat: year,
    yearsListFormat: year,
    decadeLabelFormat: (start, end) => `${dayjs(start).format(year)} – ${dayjs(end).format(year)}`,
  }
}

/**
 * On a phone a date picker opens as a dialog: anchored under its field, it ends up behind the keyboard,
 * or the keyboard scrolls the page and it loses its place
 */
const usePhone = () => useMediaQuery('(max-width: 47.99em)', false, { getInitialValueInEffect: false })

/**
 * Date input that shows and accepts Buddhist-era years in Thai
 * (24/10/2569), Gregorian in English. Values stay ISO `YYYY-MM-DD`.
 * On a phone the date is picked from the calendar only, so no keyboard comes up over it.
 */
export function DateField({ locale, ...rest }: DateFieldProps) {
  const phone = usePhone()
  if (phone) {
    return (
      <DatePickerInput
        clearable
        {...(rest as DatePickerInputProps)}
        valueFormat={dateFormat(locale, 'short')}
        {...calendarLabels(locale)}
        dropdownType="modal"
      />
    )
  }
  return <DateInput clearable {...rest} valueFormat={dateFormat(locale, 'short')} dateParser={parseThaiDate} {...calendarLabels(locale)} />
}

export interface DateTimeFieldProps extends Omit<DateTimePickerProps, 'valueFormat' | 'dropdownType'> {
  locale: Locale
}

/** Date and time in one picker, Buddhist-era years in Thai like `DateField`, a dialog on a phone */
export function DateTimeField({ locale, ...rest }: DateTimeFieldProps) {
  const phone = usePhone()
  return (
    <DateTimePicker
      clearable
      {...rest}
      valueFormat={`${dateFormat(locale, 'short')} HH:mm`}
      {...calendarLabels(locale)}
      dropdownType={phone ? 'modal' : 'popover'}
    />
  )
}

export function TimeField(props: TimeInputProps) {
  return <TimeInput {...props} />
}

export interface MoneyFieldProps extends Omit<NumberInputProps, 'thousandSeparator' | 'decimalScale'> {
  unit?: string
}

/**
 * Amounts with thousand separators and two decimals. A zero is selected when the field is entered, so typing replaces
 * it: tapping into "0.00" and typing 85 would otherwise make 850.
 */
export function MoneyField({ unit, ...rest }: MoneyFieldProps) {
  return (
    <NumberInput
      hideControls
      thousandSeparator=","
      decimalScale={2}
      fixedDecimalScale
      min={0}
      {...rest}
      onFocus={(event) => {
        const input = event.currentTarget
        if (!rest.readOnly && Number(rest.value) === 0 && rest.value !== '') requestAnimationFrame(() => input.select())
        rest.onFocus?.(event)
      }}
      rightSection={
        // tnn-keep: read-only forms hide input controls but keep the unit
        unit ? (
          <span className="tnn-keep" style={{ fontSize: 13, color: 'var(--tnn-on-surface-variant)', paddingInlineEnd: 8 }}>
            {unit}
          </span>
        ) : undefined
      }
      rightSectionWidth={unit ? 48 : undefined}
      styles={{ input: { textAlign: 'end', fontVariantNumeric: 'tabular-nums' } }}
    />
  )
}
